import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileSignature, Users, Plus, Search, Upload, GraduationCap, UserPlus, IndianRupee, Layers } from "lucide-react";
import StudentList from "./StudentList";
import StudentGrid from "./StudentGrid";
import AddToBatchModal from "./AddToBatch/AddToBatchModal";
import { useBranch } from "../../contexts/BranchContext";
import { usePlan } from "../../contexts/PlanContext";
import { useSocket } from "../../contexts/SocketContext";
import { hasPermission } from "../../utils/permissions";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import TransferBranchModal from "../Common/TransferBranchModal";
import { DOMAINS, PageHeader, AppButton, ConfirmModal, StatsCard, StatsGrid, ViewToggle, FilterPanel, FilterField, NativeSelect, FilterButton, ActiveFilterChips, EmptyState } from "../ui";
import { pendingEvaluationsLine } from "./profile/studentProfileRules";
import "./Students.scss";

const NO_BATCH_OPTION = "__none__";

// The server's `evaluation` filter: two groupings, then every status in the one
// vocabulary, so a status added there reaches this list on its own.
const EVALUATION_OPTIONS = [
  { value: "pending", label: "Pending evaluation" },
  { value: "none", label: "Not started" },
  ...Object.entries(DOMAINS.studentEvaluation).map(([value, { label }]) => ({ value, label })),
];
const evaluationLabel = (value) => EVALUATION_OPTIONS.find((o) => o.value === value)?.label || value;

// Compact INR for stat tiles (e.g. 290000 -> "₹2.9L")
const formatCompactINR = (amount) => {
  const n = Number(amount) || 0;
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(n % 1e7 === 0 ? 0 : 1)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(n % 1e5 === 0 ? 0 : 1)}L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(n % 1e3 === 0 ? 0 : 1)}K`;
  return `₹${n}`;
};

const formatTrend = (pct) => {
  if (pct === null || pct === undefined) return undefined;
  const up = pct >= 0;
  return (
    <span className={`sl-stat-trend ${up ? "sl-stat-trend--up" : "sl-stat-trend--down"}`}>
      {up ? "↑" : "↓"} {Math.abs(pct)}% vs last month
    </span>
  );
};

const StudentsPage = () => {
  const [view, setView] = useState("list");
  const [filters, setFilters] = useState({ search: "", course: "", batch: "", status: "", evaluation: "" });
  const [searchInput, setSearchInput] = useState("");
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [allBatches, setAllBatches] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [selected, setSelected] = useState([]);
  const [showAddToBatch, setShowAddToBatch] = useState(false);
  const [showBranchTransfer, setShowBranchTransfer] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();
  const { selectedBranchId, isMultiBranch, branches } = useBranch();
  const { hasPlanFeature, hasStudentModule } = usePlan();
  const { socket } = useSocket();
  const contractOn = hasStudentModule("contractStudents");
  // A refresh the person did not ask for keeps the table on screen.
  const quietRefresh = useRef(false);
  const canEnrol = hasPermission("manage_enrollments");
  const canEdit = hasPermission("add_edit_students");
  const canDelete = hasPermission("delete_students");
  const canChangeBranch = isMultiBranch && branches.length > 1 && hasPermission("manage_branches");
  const showFees = hasPlanFeature("feeManagement") && hasPermission("view_fees");

  // Fetch students from API with filters
  useEffect(() => {
    const fetchStudents = async () => {
      if (!quietRefresh.current) setLoading(true);
      quietRefresh.current = false;
      try {
        const params = {
          page,
          pageSize,
          ...(filters.search && { search: filters.search }),
          ...(filters.course && { course: filters.course }),
          ...(filters.batch && filters.batch !== NO_BATCH_OPTION && { batch: filters.batch }),
          ...(filters.batch === NO_BATCH_OPTION && { noBatch: "true" }),
          ...(filters.status && { status: filters.status }),
          ...(filters.evaluation && { evaluation: filters.evaluation }),
        };
        const { data } = await apiClient.get("/students/list", { params });
        setStudents(data.students || []);
        setTotal(data.total || 0);
        setStats(data.stats || null);
        setAllBatches(data.allBatches || []);
        setAllCourses(data.allCourses || []);
      } catch (e) {
        setToast({ show: true, message: "Failed to load students.", type: "error" });
      }
      setLoading(false);
    };

    fetchStudents();
  }, [filters, page, pageSize, selectedBranchId, refreshKey]);

  // A student submitting, or signing, changes what is pending right now: the
  // office hears through the same staff notice that rings the bell.
  useEffect(() => {
    if (!socket || !contractOn) return undefined;
    const onNotice = (data = {}) => {
      if (data.type !== "student_application") return;
      quietRefresh.current = true;
      setRefreshKey((k) => k + 1);
    };
    socket.on("notification:new", onNotice);
    return () => socket.off("notification:new", onNotice);
  }, [socket, contractOn]);

  const handleDelete = async (studentId) => {
    if (!studentId) return;
    setLoading(true);
    try {
      await apiClient.delete(`/students/${studentId}`);
      setToast({ show: true, message: "Student deleted successfully!", type: "success" });
      // Remove deleted student from list immediately
      setStudents(prev => prev.filter(s => s._id !== studentId));
    } catch (e) {
      setToast({ show: true, message: "Failed to delete student.", type: "error" });
    }
    setLoading(false);
  };

  const resetStudentPassword = (student) => {
    setConfirmAction({
      title: 'Reset Password',
      message: `Are you sure you want to reset the password for ${student.name}?`,
      variant: 'warning',
      confirmText: 'Reset',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          const response = await apiClient.put(`/users/${student.userId}/reset-password`);
          if (response.data.success) {
            const { tempPassword, emailed } = response.data;
            const where = emailed
              ? `emailed to ${student.email} (also shown here: ${tempPassword})`
              : `${tempPassword} — share it with ${student.name}`;
            setToast({ show: true, message: `Temporary password ${where}. Ask them to change it after logging in.`, type: 'success' });
          } else {
            setToast({ show: true, message: response.data.message || `Failed to reset password for ${student.email}`, type: 'warning' });
          }
        } catch (error) {
          console.error("Error resetting password:", error);
          setToast({ show: true, message: "An error occurred while resetting the password.", type: 'error' });
        }
      },
    });
  };

  const resendWelcomeEmail = (student) => {
    setConfirmAction({
      title: 'Resend Welcome Email',
      message: `Are you sure you want to resend the welcome email to ${student.name}?`,
      variant: 'info',
      confirmText: 'Resend',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          const response = await apiClient.post(`/students/${student._id}/resend-welcome-email`);
          if (response.data.success) {
            setToast({ show: true, message: `Welcome email resent to ${student.email}`, type: 'success' });
          } else {
            setToast({ show: true, message: `Failed to resend welcome email to ${student.email}`, type: 'warning' });
          }
        } catch (error) {
          console.error("Error resending welcome email:", error);
          setToast({ show: true, message: "An error occurred while resending the welcome email.", type: 'error' });
        }
      },
    });
  };

  const handleSearchBlur = () => {
    if (searchInput !== filters.search) {
      setFilters(f => ({ ...f, search: searchInput }));
      setPage(1);
    }
  };

  const activeFilterCount = (filters.course ? 1 : 0) + (filters.batch ? 1 : 0) + (filters.status ? 1 : 0) + (filters.evaluation ? 1 : 0);

  const handleResetFilters = () => {
    setFilters(f => ({ ...f, course: "", batch: "", status: "", evaluation: "" }));
    setPage(1);
  };

  const showPendingEvaluations = () => {
    setFilters(f => ({ ...f, evaluation: "pending" }));
    setPage(1);
  };

  const pendingEvaluations = contractOn ? stats?.pendingEvaluations || 0 : 0;

  // Removable chips for applied filters (search stays inline).
  const clearField = (field) => { setFilters(f => ({ ...f, [field]: "" })); setPage(1); };
  const batchLabel = (id) =>
    id === NO_BATCH_OPTION ? "No Batch Assigned" : (allBatches.find(b => b._id === id)?.name || id);
  const activeFilters = [
    filters.course && { key: "course", label: `Course: ${allCourses.find(c => c._id === filters.course)?.name || filters.course}`, onRemove: () => clearField("course") },
    filters.batch && { key: "batch", label: `Batch: ${batchLabel(filters.batch)}`, onRemove: () => clearField("batch") },
    filters.status && { key: "status", label: `Status: ${filters.status === "active" ? "Active" : "Inactive"}`, onRemove: () => clearField("status") },
    filters.evaluation && { key: "evaluation", label: `Evaluation: ${evaluationLabel(filters.evaluation)}`, onRemove: () => clearField("evaluation") },
  ].filter(Boolean);

  return (
    /* `app-page-fill` is the shell's bounded page column (styles/shell/
       _content.scss) — the same pairing `/sa/users` uses. It is what lets the
       table below scroll its own body between a sticky header and a pinned
       paginator, and what puts the horizontal scrollbar under the last row
       instead of at the bottom of a twenty-row page. */
    <div className="sl-page app-page-fill">
      <PageHeader
        icon={Users}
        title="Students"
        description="Manage and track all enrolled students"
      />

      {/* Overview stat tiles. Foldable, because on a bounded column this row
          costs the table roughly half its visible rows. */}
      {stats && (
        <StatsGrid collapsible storageKey="students" title="Overview" className="sl-stats">
          <StatsCard
            title="Total Students"
            value={stats.totalStudents}
            subtitle={formatTrend(stats.totalStudentsTrend)}
            icon={Users}
            iconColor="violet"
          />
          <StatsCard
            title="Active Students"
            value={stats.activeStudents}
            subtitle={stats.totalStudents > 0 ? `${Math.round((stats.activeStudents / stats.totalStudents) * 100)}% of total` : undefined}
            icon={GraduationCap}
            iconColor="emerald"
          />
          <StatsCard
            title="New This Month"
            value={stats.newThisMonth}
            subtitle={formatTrend(stats.newThisMonthTrend)}
            icon={UserPlus}
            iconColor="blue"
          />
          {/* Revenue gives way only while something is waiting on the office. */}
          {pendingEvaluations > 0 ? (
            <StatsCard
              title="Pending Evaluations"
              value={pendingEvaluations}
              subtitle={pendingEvaluationsLine(stats.pendingEvaluationBreakdown)}
              icon={FileSignature}
              iconColor="amber"
              onClick={showPendingEvaluations}
            />
          ) : (
            <StatsCard
              title="Total Revenue"
              value={formatCompactINR(stats.totalRevenue)}
              icon={IndianRupee}
              iconColor="rose"
            />
          )}
        </StatsGrid>
      )}

      {/* Toolbar: Filters (left) — View toggle + Actions (right) */}
      <div className="sl-toolbar">
        <div className="sl-toolbar-left">
          <div className="sl-search">
            <Search size={16} className="sl-search-icon" />
            <input
              type="text"
              className="sl-search-input"
              placeholder="Search students by name, email or phone..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onBlur={handleSearchBlur}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchBlur()}
            />
          </div>

          {/* Single filter trigger — opens the slide-over on all viewports */}
          <FilterButton count={activeFilterCount} onClick={() => setShowFilterModal(true)} />
        </div>

        <div className="sl-toolbar-right">
          {selected.length > 0 && (
            <>
              {canEnrol && (
                <AppButton
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowAddToBatch(true)}
                >
                  <Layers size={16} /> Add to batch ({selected.length})
                </AppButton>
              )}
              {canChangeBranch && (
                <AppButton
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowBranchTransfer(true)}
                >
                  Change Branch ({selected.length})
                </AppButton>
              )}
            </>
          )}

          <ViewToggle view={view} onViewChange={setView} />

          {/* Primary actions — right side */}
          {canEdit && (
            <>
              <AppButton variant="outline" size="sm" onClick={() => navigate("/students/import")}>
                <Upload size={16} /> Import
              </AppButton>
              <AppButton size="sm" onClick={() => navigate("/students/add")}>
                <Plus size={16} /> Add Student
              </AppButton>
            </>
          )}
        </div>
      </div>

      {/* Active filter chips */}
      <ActiveFilterChips items={activeFilters} onClearAll={handleResetFilters} />

      {/* Content.
          ⚠️ The `loading ? <Shimmer height={64} count={8}/>` branch is gone.
          `AppDataTable` has rendered a real <tr>/<td> shimmer since Phase 6.5,
          which keeps the column geometry so the page does not reflow when the
          rows land — and an 8 × 64px block Shimmer is 512px of unshrinkable
          content that would overflow the bounded column. The empty state is
          guarded on `!loading`, or it flashes before the first response. */}
      {!loading && students.length === 0 ? (
        /* `ui/EmptyState`, not the four hand-rolled `sl-empty-*` classes it
           replaces. Those were a second copy of the primitive at different
           sizes — an 18px title and a 14px description, against the shared
           component's 14px and 13px — on a page that already renders half a
           dozen of EmptyState's siblings. The title drops a step as a result,
           which is the point: it now matches every other empty state. */
        <EmptyState
          icon={<Users size={32} />}
          title="No students found"
          description="Try adjusting your search or filter criteria"
        />
      ) : view === "list" ? (
        <StudentList
          students={students}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          total={total}
          selected={selected}
          setSelected={setSelected}
          selectable={canEnrol || canChangeBranch}
          canEdit={canEdit}
          onDelete={canDelete ? handleDelete : undefined}
          resetStudentPassword={canEdit ? resetStudentPassword : undefined}
          resendWelcomeEmail={canEdit ? resendWelcomeEmail : undefined}
          parentPage="students"
          showEvaluation={contractOn}
          loading={loading}
          /* Only from this page: StudentList also renders inside a batch-detail
             tab, which has no height of its own. */
          fill
        />
      ) : (
        <StudentGrid
          students={students}
          canEdit={canEdit}
          onDelete={canDelete ? handleDelete : undefined}
          page={page}
          setPage={setPage}
          total={total}
          pageSize={pageSize}
          /* The cards scroll in the same bounded region the table does, so
             switching List ↔ Grid does not move the paginator. */
          fill
        />
      )}

      {/* ⚠️ The grid view's `sl-footer` is gone. It rendered a THIRD "Showing N
          of M entries" — at 0.875rem, beside `Paginator`'s own info line at
          0.8125rem and `.pagination-info`'s raw 14px — and only when `total <=
          pageSize`, which is exactly the case a paginator hides itself.
          `StudentGrid` passes `showSinglePage` now, as `AppDataTable` already
          does for the list, so the count is said once, at one size, in both
          views. */}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(t => ({ ...t, show: false }))}
      />
      {canEnrol && (
        <AddToBatchModal
          show={showAddToBatch}
          onHide={() => setShowAddToBatch(false)}
          studentIds={selected}
          showFees={showFees}
          onSaved={({ message, closed }) => {
            setSelected([]);
            setRefreshKey(k => k + 1);
            if (closed) setToast({ show: true, message, type: "success" });
          }}
        />
      )}
      <TransferBranchModal
        show={showBranchTransfer}
        onHide={() => setShowBranchTransfer(false)}
        selectedIds={selected}
        mode="students"
        onTransfer={() => {
          setSelected([]);
          setRefreshKey(k => k + 1);
          setToast({ show: true, message: "Students moved to new branch!", type: "success" });
        }}
      />
      <FilterPanel
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        subtitle={contractOn ? "Filter students by course, batch, status and evaluation." : "Filter students by course, batch and status."}
        onReset={handleResetFilters}
        appliedCount={activeFilterCount}
      >
        <FilterField label="Course">
          <NativeSelect size="lg"
            value={filters.course}
            onChange={(e) => {
              setFilters(f => ({ ...f, course: e.target.value }));
              setPage(1);
            }}
          >
            <option value="">All Courses</option>
            {allCourses.map(c => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </NativeSelect>
        </FilterField>
        <FilterField label="Batch">
          <NativeSelect size="lg"
            value={filters.batch}
            onChange={(e) => {
              setFilters(f => ({ ...f, batch: e.target.value }));
              setPage(1);
            }}
          >
            <option value="">All Batches</option>
            <option value={NO_BATCH_OPTION}>No Batch Assigned</option>
            {allBatches.map(b => (
              <option key={b._id} value={b._id}>{b.name}</option>
            ))}
          </NativeSelect>
        </FilterField>
        <FilterField label="Status">
          <NativeSelect size="lg"
            value={filters.status}
            onChange={(e) => {
              setFilters(f => ({ ...f, status: e.target.value }));
              setPage(1);
            }}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </NativeSelect>
        </FilterField>
        {contractOn && (
          <FilterField label="Evaluation">
            <NativeSelect size="lg"
              value={filters.evaluation}
              onChange={(e) => {
                setFilters(f => ({ ...f, evaluation: e.target.value }));
                setPage(1);
              }}
            >
              <option value="">All Students</option>
              {EVALUATION_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </NativeSelect>
          </FilterField>
        )}
      </FilterPanel>

      <ConfirmModal
        show={!!confirmAction}
        onHide={() => setConfirmAction(null)}
        onConfirm={() => confirmAction?.onConfirm()}
        title={confirmAction?.title || ''}
        message={confirmAction?.message || ''}
        confirmText={confirmAction?.confirmText || 'Confirm'}
        variant={confirmAction?.variant || 'danger'}
      />
    </div>
  );
};

export default StudentsPage;
